import * as THREE from 'three';
import { CONFIG } from '../../config';
import {
  type Dinosaur,
  type DinosaurSpecies,
  gameState,
} from '../../state/game-state';
import { mulberry32, pickWeighted, randRange } from '../../utils/random';
import { SPECIES_CONFIG, SPECIES_LIST } from './dinosaur-types';
import { buildDinosaur } from './dinosaur-factory';

let idCounter = 0;
function nextId(): string {
  return `d-${idCounter++}`;
}

function pickAngle(rng: () => number): number {
  return rng() * Math.PI * 2;
}

export class DangerRingSystem {
  public root: THREE.Group;
  private rings: THREE.Mesh[] = [];
  private ringGeo: THREE.RingGeometry;
  private innerGeo: THREE.RingGeometry;
  private innerRings: THREE.Mesh[] = [];
  private maxRings = 8;
  private dangerRadius: number;

  constructor() {
    this.root = new THREE.Group();
    this.dangerRadius = CONFIG.dinosaur.dangerRadius;
    this.ringGeo = new THREE.RingGeometry(0.85, 1.0, 32);
    this.innerGeo = new THREE.RingGeometry(0.7, 0.78, 32);
    for (let i = 0; i < this.maxRings; i++) {
      const ringMat = new THREE.MeshBasicMaterial({
        color: 0xff3322,
        transparent: true,
        opacity: 0.7,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      const ring = new THREE.Mesh(this.ringGeo, ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = 0.03;
      ring.visible = false;
      ring.renderOrder = 5;
      this.root.add(ring);
      this.rings.push(ring);

      const innerMat = new THREE.MeshBasicMaterial({
        color: 0xff6644,
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      const innerRing = new THREE.Mesh(this.innerGeo, innerMat);
      innerRing.rotation.x = -Math.PI / 2;
      innerRing.position.y = 0.04;
      innerRing.visible = false;
      innerRing.renderOrder = 5;
      this.root.add(innerRing);
      this.innerRings.push(innerRing);
    }
  }

  update(now: number, dinosaurs: Dinosaur[], orb: THREE.Vector3): void {
    const sorted = dinosaurs
      .map((d) => ({ d, dist: Math.hypot(d.position[0] - orb.x, d.position[2] - orb.z) }))
      .filter((x) => x.dist < this.dangerRadius)
      .sort((a, b) => a.dist - b.dist);

    for (let i = 0; i < this.maxRings; i++) {
      const ring = this.rings[i];
      const inner = this.innerRings[i];
      if (i < sorted.length) {
        const { d, dist } = sorted[i];
        const intensity = 1 - dist / this.dangerRadius;
        const size = (1.4 + intensity * 1.2) * d.scale;
        ring.visible = true;
        inner.visible = true;
        ring.position.set(d.position[0], 0.03, d.position[2]);
        inner.position.set(d.position[0], 0.04, d.position[2]);
        ring.scale.set(size, size, 1);
        inner.scale.set(size * 0.95, size * 0.95, 1);
        const pulse = 0.5 + 0.5 * Math.sin(now * 0.012 + i * 0.7);
        const ringMat = ring.material as THREE.MeshBasicMaterial;
        const innerMat = inner.material as THREE.MeshBasicMaterial;
        ringMat.opacity = 0.5 + 0.4 * intensity + pulse * 0.25;
        innerMat.opacity = 0.3 + 0.3 * intensity;
        ring.rotation.z = now * 0.003 + i;
        inner.rotation.z = -now * 0.005 - i;
      } else {
        ring.visible = false;
        inner.visible = false;
      }
    }
  }
}

export class DinosaurSystem {
  public root: THREE.Group;
  public dinosaurs: Dinosaur[] = [];
  public orbPos: THREE.Vector3 = new THREE.Vector3();
  public totalSpawned = 0;
  public dangerRings: DangerRingSystem;
  private meshes = new Map<string, THREE.Group>();
  private rng: () => number;
  private nextSpawnAt = 0;
  private nextCompyGroupAt = 0;
  private nextTrexAt = 0;
  private trexSpawned = false;
  private legPhase = 0;
  private recentSpawnChain: DinosaurSpecies[] = [];

  constructor(seed: number = 1234) {
    this.root = new THREE.Group();
    this.dangerRings = new DangerRingSystem();
    this.rng = mulberry32(seed);
    this.nextSpawnAt = performance.now() + 2500;
    this.nextCompyGroupAt =
      performance.now() +
      randRange(this.rng, ...CONFIG.dinosaur.compyGroupIntervalMs) / 2 +
      15000;
    this.nextTrexAt = performance.now() + CONFIG.dinosaur.trexFirstAppearMs;
  }

  count(): number {
    return this.dinosaurs.length;
  }

  spawnLimitReached(): boolean {
    return this.totalSpawned >= CONFIG.run.totalDinosaurs;
  }

  triggerBeatSpawn(difficulty: number): void {
    if (this.spawnLimitReached()) return;
    if (this.dinosaurs.length >= CONFIG.dinosaur.maxOnScreen) return;

    const baseSpawn = 1;
    const extraSpawn = Math.floor(difficulty * 2.5);
    const total = Math.max(1, Math.min(CONFIG.run.maxSpawnPerBeat, baseSpawn + extraSpawn));

    for (let i = 0; i < total; i++) {
      if (this.dinosaurs.length >= CONFIG.dinosaur.maxOnScreen) break;
      if (difficulty > 0.5 && this.rng() < 0.12 && !this.dinosaurs.some((d) => d.species === 'trex')) {
        this.spawn('trex');
        this.recordSpawn('trex');
        continue;
      }
      if (
        difficulty >= CONFIG.run.armoredMinDifficulty &&
        this.rng() < CONFIG.run.armoredSpawnRate &&
        this.canSpawnArmored()
      ) {
        this.spawn('ankylosaurus');
        this.recordSpawn('ankylosaurus');
        continue;
      }
      const speciesPool = SPECIES_LIST.filter((s) => s !== 'trex' && s !== 'ankylosaurus');
      const weights = speciesPool.map((s) => SPECIES_CONFIG[s].spawnWeight);
      const species = pickWeighted(this.rng, speciesPool, weights);
      this.spawn(species);
      this.recordSpawn(species);
    }
  }

  spawn(species: DinosaurSpecies, overridePos?: THREE.Vector3): void {
    if (this.spawnLimitReached()) return;
    const cfg = SPECIES_CONFIG[species];
    const scale = randRange(this.rng, cfg.scaleRange[0], cfg.scaleRange[1]);
    const id = nextId();
    const cx = this.orbPos.x;
    const cz = this.orbPos.z;
    const angle = overridePos
      ? Math.atan2(overridePos.z - cz, overridePos.x - cx)
      : pickAngle(this.rng);
    const radius = overridePos
      ? Math.hypot(overridePos.x - cx, overridePos.z - cz)
      : CONFIG.dinosaur.spawnRadius;
    const x = overridePos ? overridePos.x : cx + Math.cos(angle) * radius;
    const z = overridePos ? overridePos.z : cz + Math.sin(angle) * radius;
    const pos: [number, number, number] = [x, 0, z];
    const dino: Dinosaur = {
      id,
      species,
      state: 'wander',
      position: pos,
      velocity: [0, 0, 0],
      stunUntil: 0,
      createdAt: performance.now(),
      scale,
      armorBroken: false,
    };
    this.dinosaurs.push(dino);
    this.totalSpawned += 1;
    gameState.registerSpawn();
    const mesh = buildDinosaur(species, scale);
    mesh.position.set(x, 0, z);
    this.meshes.set(id, mesh);
    this.root.add(mesh);
  }

  spawnCompyGroup(): void {
    if (this.spawnLimitReached()) return;
    const remaining = CONFIG.run.totalDinosaurs - this.totalSpawned;
    if (remaining <= 0) return;
    const baseAngle = pickAngle(this.rng);
    const desired = 3 + Math.floor(this.rng() * 4);
    const count = Math.min(desired, remaining);
    for (let i = 0; i < count; i++) {
      const angle = baseAngle + (i / Math.max(1, count)) * 0.8 - 0.4;
      const radius = CONFIG.dinosaur.spawnRadius * 0.8;
      const x = this.orbPos.x + Math.cos(angle) * radius;
      const z = this.orbPos.z + Math.sin(angle) * radius;
      this.spawn('compsognathus', new THREE.Vector3(x, 0, z));
    }
  }

  private maybeSpawn(_now: number): void {
    void _now;
  }

  private maybeSpawnSpecials(now: number): void {
    if (this.spawnLimitReached()) return;
    if (now >= this.nextCompyGroupAt) {
      this.spawnCompyGroup();
      const [minS, maxS] = CONFIG.dinosaur.compyGroupIntervalMs;
      this.nextCompyGroupAt = now + randRange(this.rng, minS, maxS);
    }
    if (!this.trexSpawned && now >= this.nextTrexAt && this.totalSpawned < CONFIG.run.totalDinosaurs) {
      this.spawn('trex');
      this.trexSpawned = true;
    } else if (this.trexSpawned && now >= this.nextTrexAt) {
      const aliveTrex = this.dinosaurs.find((d) => d.species === 'trex');
      if (!aliveTrex && this.totalSpawned < CONFIG.run.totalDinosaurs) {
        this.spawn('trex');
      }
      const [minS, maxS] = CONFIG.dinosaur.trexIntervalMs;
      this.nextTrexAt = now + randRange(this.rng, minS, maxS);
    }
  }

  applyShockwave(
    origin: THREE.Vector3,
    radius: number,
    knockback: number,
    now: number,
    defeat: boolean = false,
    breakArmor: boolean = false
  ): { hitIds: string[]; hitPositions: [number, number, number][]; defeatedIds: string[]; armoredHits: number } {
    const hitIds: string[] = [];
    const hitPositions: [number, number, number][] = [];
    const defeatedIds: string[] = [];
    let armoredHits = 0;
    for (const d of this.dinosaurs) {
      const dx = d.position[0] - origin.x;
      const dz = d.position[2] - origin.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist > radius) continue;
      const cfg = SPECIES_CONFIG[d.species];
      hitIds.push(d.id);
      hitPositions.push(d.position);
      if (breakArmor && cfg.armored && !d.armorBroken) {
        d.armorBroken = true;
        armoredHits++;
        this.flashArmorBroken(d.id);
        continue;
      }
      if (defeat) {
        if (cfg.armored && !d.armorBroken) {
          armoredHits++;
          if (knockback > 0 && dist > 0.01) {
            const factor = (1 - dist / radius) * (knockback * 0.4);
            d.position[0] += (dx / dist) * factor;
            d.position[2] += (dz / dist) * factor;
          }
          d.stunUntil = now + CONFIG.dinosaur.stunDurationMs;
          d.state = 'stun';
          continue;
        }
        defeatedIds.push(d.id);
        continue;
      }
      if (knockback > 0 && dist > 0.01) {
        const factor = (1 - dist / radius) * knockback;
        d.position[0] += (dx / dist) * factor;
        d.position[2] += (dz / dist) * factor;
      }
      if (knockback > 0) {
        d.stunUntil = now + CONFIG.dinosaur.stunDurationMs;
        d.state = 'stun';
      }
    }
    for (const id of defeatedIds) {
      this.removeDinosaur(id);
    }
    if (defeatedIds.length > 0) {
      gameState.registerDefeat();
    }
    return { hitIds, hitPositions, defeatedIds, armoredHits };
  }

  private flashArmorBroken(id: string): void {
    const mesh = this.meshes.get(id);
    if (!mesh) return;
    mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const mat = child.material as THREE.MeshStandardMaterial;
        if (mat && mat.emissive) {
          mat.emissive.set(0xff4422);
          mat.emissiveIntensity = 1.2;
        }
      }
    });
  }

  removeDinosaur(id: string): void {
    const idx = this.dinosaurs.findIndex((d) => d.id === id);
    if (idx >= 0) this.dinosaurs.splice(idx, 1);
    const mesh = this.meshes.get(id);
    if (mesh) {
      this.root.remove(mesh);
      this.meshes.delete(id);
    }
  }

  update(now: number, deltaMs: number, orb: THREE.Vector3): void {
    this.maybeSpawn(now);
    this.maybeSpawnSpecials(now);
    this.legPhase += deltaMs * 0.01;
    const dt = deltaMs * 0.001;

    const gs = gameState.get();
    const orbActive = !gs.isRespawning && !gs.isTimeUp && !gs.isRunComplete;

    for (const d of this.dinosaurs) {
      if (d.state === 'stun') {
        if (now >= d.stunUntil) {
          d.state = 'wander';
        }
        const mesh = this.meshes.get(d.id);
        if (mesh) mesh.position.set(d.position[0], 0, d.position[2]);
        continue;
      }

      if (d.state === 'reached_orb') {
        gameState.addCorruption(SPECIES_CONFIG[d.species].corruptionOnReach * dt * 0.2);
        const mesh = this.meshes.get(d.id);
        if (mesh) {
          mesh.position.set(d.position[0], 0, d.position[2]);
          mesh.rotation.y += dt * 1.5;
        }
        continue;
      }

      const cfg = SPECIES_CONFIG[d.species];
      const dx = orb.x - d.position[0];
      const dz = orb.z - d.position[2];
      const dist = Math.sqrt(dx * dx + dz * dz);
      const dirX = dist > 0.01 ? dx / dist : 0;
      const dirZ = dist > 0.01 ? dz / dist : 0;

      if (dist < CONFIG.dinosaur.reachOrbDistance && orbActive) {
        d.state = 'reached_orb';
      } else {
        d.state = dist < 12 ? 'charge' : 'notice';
        const speed = cfg.speed * (d.state === 'charge' ? 1.4 : 0.6);
        d.position[0] += dirX * speed * dt;
        d.position[2] += dirZ * speed * dt;
      }

      const mesh = this.meshes.get(d.id);
      if (mesh) {
        mesh.position.set(d.position[0], 0, d.position[2]);
        if (dist > 0.1) {
          mesh.rotation.y = Math.atan2(dirX, dirZ);
        }
        this.animateLegs(mesh, d.state);
      }
    }

    this.dangerRings.update(now, this.dinosaurs, orb);
  }

  private animateLegs(mesh: THREE.Group, state: Dinosaur['state']): void {
    const speed = state === 'charge' ? 14 : 6;
    const amp = state === 'charge' ? 0.3 : 0.12;
    const legs = mesh.children.filter((c) => c.name.startsWith('leg-'));
    for (let i = 0; i < legs.length; i++) {
      const phase = (i % 2 === 0 ? 0 : Math.PI) + this.legPhase * speed * 0.05;
      const swing = Math.sin(phase) * amp;
      const leg = legs[i] as THREE.Mesh;
      leg.rotation.x = swing;
    }
  }

  private recordSpawn(species: DinosaurSpecies): void {
    this.recentSpawnChain.push(species);
    const maxChain = CONFIG.run.maxConsecutiveArmored * 2 + 1;
    if (this.recentSpawnChain.length > maxChain) {
      this.recentSpawnChain.shift();
    }
  }

  private canSpawnArmored(): boolean {
    const maxArmored = CONFIG.run.maxConsecutiveArmored;
    let tailArmored = 0;
    for (let i = this.recentSpawnChain.length - 1; i >= 0; i--) {
      if (this.recentSpawnChain[i] === 'ankylosaurus') tailArmored++;
      else break;
    }
    if (tailArmored >= maxArmored) return false;
    const aliveArmored = this.dinosaurs.filter((d) => d.species === 'ankylosaurus').length;
    if (aliveArmored >= 2) return false;
    return true;
  }
}
